import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, List, Modal } from "semantic-ui-react";
import { DatePicker } from "../../../components/shared/DatePicker";
import WeekDaysSelect from "../../../components/shared/WeekDaysSelect";
import { useMessageResources, useTextResources } from "../../../language";
import PropTypes from "prop-types";
import { Designations, RatePricingTypesEnum } from "../../../components/shared/constants";
import moment from "moment";
import NumericInput from "../../../components/shared/NumericInput";
import { handleStartEndChange, filterDateRangesByDateRange }
  from "../../../components/shared/utils";
import { ExtraPersonPriceEdit } from "./ExtraPersonPriceEdit";
import { PriceListContext } from "../context";
import { onError, onWarning } from "../../../components/shared/messages";
import { useConfirm } from "../../../hooks/useConfirm";
import { adultsDesignations, childrenDesignations, infantsDesignations } from "../consts";
import PricingElementPanel from "../../../components/shared/PricingElementPanel";
import { RPEsRearangeDateRanges, setRPEsWithRearangedExceptions, insertCopiedPricing, areAllStartEndDatesValid, hasZeroPrice } from "./pricingUtils";
import { PriceElementsList } from "./PriceElementsList";


export const PricingPerPersonPopup = ({ open, onClose, EntityTypeId, EntityId, updateRPEs, Packages, rateElementName, onDelete, onPricingSave }) => {
  const confirm = useConfirm();
  const text = useTextResources();
  const message = useMessageResources();
  const { state } = useContext(PriceListContext);
  const [fromFilterDate, setFromFilterDate] = useState();
  const [toFilterDate, setToFilterDate] = useState();
  const [pricings, setPricings] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().add(1, "day").toDate());
  const [selectedDays, setSelectedDays] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  //price to update according to IsProductPricingIncludesTax hotelParameter
  const priceToUpdate = useMemo(() => {
    return state?.IsProductPricingIncludesTax ? "AmountAfterTax" : "AmountBeforeTax";
  }, [state?.IsProductPricingIncludesTax]);

  useEffect(() => {
    if (open) {
      (async () => {
        try {
          const RatePricingElements = state.RatePricingElements.filter(rpe =>
            rpe.EntityTypeId === EntityTypeId &&
            rpe.EntityId === EntityId &&
            Boolean(rpe.RatePricingElementsPackages?.length) === Boolean(Packages?.length) &&
            rpe.IsActive);
          const RateElementsWithFormmated = await RatePricingElements.map(ratePricingElement => {
            let ratePerPeople = ratePricingElement.RatePerPeople;
            let fittingRatePerPerson = ratePerPeople?.find(x => !x.ExceptionDate);

            if (!fittingRatePerPerson)
              fittingRatePerPerson = {
                RatePricingElementId: ratePricingElement.Id || 0,
                RatePerPersonPrices: [],
                RateId: state.Id,
                RoomTypeId: EntityId,
              };
            const nonDesignatedPrice = priceToUpdate === "AmountAfterTax" ? "AmountBeforeTax" : "AmountAfterTax";
            fittingRatePerPerson[nonDesignatedPrice] = 0;
            const exceptionRatePerPeople = ratePricingElement?.RatePerPeople?.filter(r => r.ExceptionDate) ?? [];
            return {
              ...ratePricingElement,
              StartDate: ratePricingElement.StartDate,
              EndDate: ratePricingElement.EndDate,
              RatePerPeople: [fittingRatePerPerson, ...exceptionRatePerPeople]
            };
          });
          setPricings(JSON.parse(JSON.stringify(RateElementsWithFormmated.sort((a, b) => a.StartDate < b.StartDate ? -1 : 1))));
        } catch (error) {
          error.status === 400 ? onWarning(error) : onError(error);
        }

      })();
    } else {
      setPricings([]);
      setIsDirty(false);
    }
  }, [state.RatePricingElements, EntityTypeId, EntityId, open])

  const onSave = async (isClose) => {
    await updateRPEs({ rpes: pricings, isClose, setIsDirty, validate: isValidBeforeSave, setPricings });
  };

  const isValidBeforeSave = async () => {
    //check if some prices are 0
    let shouldContinue = true;
    let mendatoryDesignatedPrices = [Designations.FirstExtraAdult, Designations.FirstExtraChild, Designations.FirstExtraInfant];
    let requiredDesignatedPrices = [Designations.Single, Designations.Double];

    if (pricings.some(x => x.isEdit)) {
      onWarning(message("ApproveOpenPricingsBeforeSave"));
      return false;
    }

    if (!state.BaseRateId && hasZeroPrice(pricings, requiredDesignatedPrices, RatePricingTypesEnum.PerPerson, priceToUpdate)) {
      onWarning(text("SingleDoubleIsMendatory"));
      return false;
    }

    if (!areAllStartEndDatesValid(pricings)) {
      onWarning(message("DatesIsMandatoryField"));
      return false;
    }

    if (hasZeroPrice(pricings, mendatoryDesignatedPrices, RatePricingTypesEnum.PerPerson, priceToUpdate)) {
      shouldContinue = await confirm({
        header: message("SomePricesAreZero"),
        content: message("WouldYouLikeToContinue"),
        confirmButton: {
          content: text('Yes'),
          positive: true,
        },
        cancelButton: {
          content: text('No'),
          negative: true,
        },
      });
    }

    return shouldContinue;
  }

  const memoDatesForWeekComponent = useMemo(
    () => (startDate && endDate ? [{ startDate, endDate }] : []),
    [startDate, endDate]
  );

  const addPricing = () => {
    const newRpe = {
      Id: 0,
      TempId: `newPricingTempId-${crypto.randomUUID()}`,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: selectedDays,
      RatePerPeople: [{
        RatePricingElementId: 0,
        RatePerPersonPrices: [],
        RateId: state.Id,
        RoomTypeId: EntityId,
      }],
      RatePricingElementsPackages: Packages,
      PricingTemplateId: pricings.find(x => x.IsActive)?.PricingTemplateId,
      RatePerPersonId: 0,
      RatePricingTypeId: RatePricingTypesEnum.PerPerson,
      RateId: state.Id,
      EntityId,
      EntityTypeId,
      IsActive: true,
      IsNew: true
    }

    const newPricings = RPEsRearangeDateRanges(newRpe, pricings, state.BaseRateId);
    setRPEsWithRearangedExceptions(newPricings, 'RatePerPeople')
    setPricings(newPricings)

    setIsDirty(true);
  };

  const onCopy = (oldPricing, newPricing) => {
    const rpes = insertCopiedPricing(pricings, oldPricing, newPricing);
    setPricings(rpes);
    setIsDirty(true)
  }

  const onPricingSaveClicked = (pricing) => {
    onPricingSave({ rpe: pricing, rpes: pricings, setPricings, setIsDirty, name: 'RatePerPeople' });
  }

  const changeDesignatedPrice = useCallback((rpe, designationId, value) => {
    setIsDirty(true);
    rpe.IsUpdated = true;
    const ratePerPerson = rpe.RatePerPeople[0];

    if (value === null || value === undefined) {
      ratePerPerson.RatePerPersonPrices = ratePerPerson.RatePerPersonPrices.filter(x => x.DesignationId !== designationId);
    }
    else {
      const fittingDesignatedPrice = ratePerPerson.RatePerPersonPrices.find(x => x.DesignationId === designationId);
      if (fittingDesignatedPrice)
        fittingDesignatedPrice[priceToUpdate] = value;
      else
        ratePerPerson.RatePerPersonPrices.push({
          Id: 0,
          RatePerPersonId: ratePerPerson.Id,
          DesignationId: designationId,
          AmountAfterTax: 0,
          AmountBeforeTax: 0,
          [priceToUpdate]: value
        })
    }


    setPricings([...pricings]);
  }, [pricings])

  const getPriceByDesignationId = useCallback((rpe, designationId) => {
    return rpe.RatePerPeople?.[0]?.RatePerPersonPrices?.find(x => x.DesignationId === designationId)?.[priceToUpdate];
  }, [pricings]);

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm(
        {
          content: `${message("WouldYouLikeToContinue")}?`,
          confirmButton: {
            content: text('Yes'),
            positive: true,
          },
          cancelButton: {
            content: text('No'),
            negative: true,
          },
        }))
        onClose();
    }
    else onClose();
  }

  const inputClassName = "property-input-highlighted";
  return (
    <Modal size="large" open={open} dimmer centered className="price-list-settings">
      <Modal.Header>
        {`${text("SetPerPersonPrice")} - ${rateElementName}`}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths={4}>
            <DatePicker
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("From")}
              onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
              clearable={false}
              value={startDate}
            />
            <DatePicker
              minDate={moment(startDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("To")}
              onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
              clearable={false} value={endDate}
            />
            <div className="weekdays-select">
              <WeekDaysSelect onChange={setSelectedDays} dates={memoDatesForWeekComponent} />
              <div className="add-pricing-button">
                <Button primary onClick={addPricing} disabled={selectedDays?.length === 0}>{text("Add")}</Button>
              </div>
            </div>
          </Form.Group>
          <Form.Group widths={4} >
            <DatePicker
              label={`${text("FilterBy")} ${text("FromDate")}`}
              value={fromFilterDate}
              onChange={(value) => handleStartEndChange([value, toFilterDate], 0, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <DatePicker
              label={`${text("FilterBy")} ${text("ToDate")}`}
              value={toFilterDate}
              onChange={(value) => handleStartEndChange([fromFilterDate, value], 1, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(fromFilterDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <div style={{ marginLeft: 5, alignSelf: "flex-end" }}>
              <Button primary onClick={() => {
                setFromFilterDate();
                setToFilterDate();
              }}>{text("Clear")}</Button>
            </div>
            <Form.Field></Form.Field>
          </Form.Group>
          <PriceElementsList state={state} pricings={pricings}>
              {filterDateRangesByDateRange(pricings, fromFilterDate, toFilterDate)?.filter(x => x.IsActive && !x.IsOverride).map((pricing) => {
                const isDerived = pricing.RateId === state.BaseRateId;
                return (
                  <List.Item key={pricing.Id || pricing.TempId}>
                    <PricingElementPanel
                      pricing={pricing}
                      onDelete={() => onDelete(pricing, pricings, setPricings)}
                      isDerived={isDerived}
                      onSave={onPricingSaveClicked}
                      onCopy={onCopy}
                      setPricings={setPricings}
                    >
                      <List.Content>
                        <Form>
                          <Form.Group widths="equal" style={{ display: "flex", alignItems: "center" }}>
                            <Form.Field label={text("Single")}
                              buttonPlacement="none" selectOnFocus
                              disabled={isDerived} control={NumericInput}
                              required={!state.BaseRateId} nullable={state.BaseRateId}
                              value={getPriceByDesignationId(pricing, Designations.Single)}
                              onChangeComplete={(value) => changeDesignatedPrice(pricing, Designations.Single, value)}
                              className={inputClassName}
                            />
                            <Form.Field label={text("Double")}
                              buttonPlacement="none" selectOnFocus
                              disabled={isDerived} control={NumericInput}
                              required={!state.BaseRateId} nullable={state.BaseRateId}
                              value={getPriceByDesignationId(pricing, Designations.Double)}
                              onChangeComplete={(value) => changeDesignatedPrice(pricing, Designations.Double, value)}
                              className={inputClassName}
                            />
                            <Form.Field
                              isDerived={isDerived}
                              label={`${text("Extra")}  ${text("Adults")}`}
                              onChange={(designationId, value) => changeDesignatedPrice(pricing, designationId, value)}
                              control={ExtraPersonPriceEdit}
                              nullable
                              formFieldExtraProps={{ min: 0 }}
                              designationIds={adultsDesignations}
                              getPriceByDesignationId={(designationId) => getPriceByDesignationId(pricing, designationId)}
                              className={inputClassName}
                            />
                            <Form.Field
                              isDerived={isDerived}
                              label={`${text("Extra")} ${text("Children")}`}
                              onChange={(designationId, value) => changeDesignatedPrice(pricing, designationId, value)}
                              control={ExtraPersonPriceEdit}
                              nullable
                              formFieldExtraProps={{ min: 0 }}
                              designationIds={childrenDesignations}
                              getPriceByDesignationId={(designationId) => getPriceByDesignationId(pricing, designationId)}
                              className={inputClassName}
                            />
                            <Form.Field
                              isDerived={isDerived}
                              label={`${text("Extra")} ${text("Infants")}`}
                              onChange={(designationId, value) => changeDesignatedPrice(pricing, designationId, value)}
                              control={ExtraPersonPriceEdit}
                              nullable
                              formFieldExtraProps={{ min: 0 }}
                              designationIds={infantsDesignations}
                              getPriceByDesignationId={(designationId) => getPriceByDesignationId(pricing, designationId)}
                              className={inputClassName}
                            />
                          </Form.Group>
                        </Form>
                      </List.Content>
                    </PricingElementPanel>
                  </List.Item>)
              })}
          </PriceElementsList>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          {text("Close")}
        </Button>
        <Button positive onClick={() => onSave(true)}>
          {text("Saveandclose")}
        </Button>
        <Button positive onClick={() => onSave()}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

PricingPerPersonPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateRPEs: PropTypes.func,
  EntityTypeId: PropTypes.number,
  EntityId: PropTypes.number,
  Packages: PropTypes.array,
  rateElementName: PropTypes.string,
  onDelete: PropTypes.func,
  onPricingSave: PropTypes.func
};
